import * as React from "react"
import { useRouteMatch } from "react-router-dom"
import { SvgMessageQuestionCircle } from "@digits-shared/components/SVGIcons/line/MessageQuestionCircle.svg"
import { SvgSettings01 } from "@digits-shared/components/SVGIcons/line/Settings01.svg"
import { SvgUserSquare } from "@digits-shared/components/SVGIcons/line/UserSquare.svg"
import envHelper from "@digits-shared/helpers/envHelper"
import useSession from "@digits-shared/hooks/useSession"
import styled from "styled-components"
import { SIDEBAR_PATH_SVG_STYLES } from "src/frontend/components/Shared/NavSidebar/sidebarConstants"
import {
  SidebarAnchorRow,
  SidebarRow,
} from "src/frontend/components/Shared/NavSidebar/SidebarExpando"
import { RequiresUpdate } from "src/frontend/components/Shared/Permissions/Requires"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import {
  FrontendPermissionModule,
  FrontendPermissionSource,
} from "src/frontend/session/permissionModule"
import { Experience } from "src/frontend/session/personas"

/*
  STYLES
*/

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  width: 100%;

  svg {
    ${SIDEBAR_PATH_SVG_STYLES};
  }
`

/*
  COMPONENT
*/

export const SidebarFooter: React.FC = () => (
  <Footer>
    <SidebarSettings />
    <SidebarCustomerSupportLink />
    <SidebarDigitsOnly />
  </Footer>
)

const SidebarSettings: React.FC = () => {
  const isOnFirmDashboard = useRouteMatch(routes.firmDashboardRoot.parameterizedPath)
  if (isOnFirmDashboard) return null

  return <SidebarEntitySettings />
}

export const SharedUserFooter: React.FC = () => (
  <Footer>
    <SidebarCustomerSupportLink />
    <SidebarDigitsOnly />
  </Footer>
)

const SidebarCustomerSupportLink: React.FC = () => (
  <SidebarAnchorRow
    Icon={SvgMessageQuestionCircle}
    href={useHelpCenterLink()}
    target="_blank"
    rel="noopener noreferrer"
  >
    Help Center
  </SidebarAnchorRow>
)

function useHelpCenterLink() {
  const { currentPrimaryExperience } = useSession<FrontendSession>()
  switch (currentPrimaryExperience) {
    case Experience.AccountantPortal:
      return "https://help.digits.com/support/solutions/folders/151000555451"
    case Experience.OperatorPortal:
      return "https://help.digits.com/support/solutions/folders/151000555437"
    case Experience.ClientPortal:
      return "https://help.digits.com/support/home"
  }
}

const SidebarDigitsOnly: React.FC = () => {
  const { isDigitsEmployee } = useSession<FrontendSession>()
  // DO NOT remove the isProduction check
  const displayDigitsSupport = !envHelper.isProduction() || isDigitsEmployee

  if (!displayDigitsSupport) {
    return null
  }

  return (
    <SidebarRow Icon={SvgUserSquare} route={routes.internalFeatures}>
      Employee Features
    </SidebarRow>
  )
}

const SidebarEntitySettings: React.FC = () => {
  const { currentLegalEntity } = useSession<FrontendSession>()
  if (!currentLegalEntity?.id) return null
  const params = { leSlug: currentLegalEntity.slug }

  return (
    <RequiresUpdate
      source={FrontendPermissionSource.LegalEntity}
      module={FrontendPermissionModule.Organizations}
    >
      <SidebarRow Icon={SvgSettings01} route={routes.legalEntitySettings} params={params}>
        Settings
      </SidebarRow>
    </RequiresUpdate>
  )
}
